import { fetchAndActivate, getValue } from 'firebase/remote-config';
import { remoteConfig } from './firebase';
import CryptoJS from 'crypto-js';
import log from '../utils/logger';

// Constants
const CLIENT_ID = import.meta.env.VITE_CLIENT_ID;
const SECRET_KEY = import.meta.env.VITE_SECRET_KEY;

// Cache per rs value
let cachedRs: string | null = null;

// Cache per deviceID
let cachedDeviceId: string | null = null;

// Funzione per ottenere timestamp come in Swift
export const getTimestamp = (): string => {
    // Usa timeIntervalSince1970 come in Swift (secondi dal 1970)
    const currentTimeStampString = (Date.now() / 1000).toString();
    // Genera un valore random come stringa
    const randomValueString = Math.floor(Math.random() * 4294967295).toString();
    // Concatena timestamp e valore random
    return currentTimeStampString + randomValueString;
};

// Funzione per ottenere rs da Remote Config
export const getRsFromRemoteConfig = async (): Promise<string> => {
    if (cachedRs) {
        return cachedRs;
    }

    try {
        // Configura Remote Config
        remoteConfig.settings = {
            minimumFetchIntervalMillis: 3600000, // 1 ora
            fetchTimeoutMillis: 60000, // 60 secondi
        };

        // Fetch e attiva i valori
        await fetchAndActivate(remoteConfig);

        // Ottieni il valore di 'rs'
        const rsValue = getValue(remoteConfig, 'rs');
        cachedRs = rsValue.asString();

        //log.debug('🔧 Remote Config rs:', cachedRs);
        return cachedRs;
    } catch (error) {
        log.error('❌ Errore recupero Remote Config:', error);
        // Fallback value in caso di errore
        cachedRs = 'default_rs_value';
        return cachedRs;
    }
};

// Funzione per generare/recuperare deviceID
export const getDeviceId = (): string => {
    if (cachedDeviceId) {
        return cachedDeviceId;
    }

    // Controlla se esiste già un deviceID nel localStorage
    const storedDeviceId = localStorage.getItem('deviceID');
    if (storedDeviceId) {
        cachedDeviceId = storedDeviceId;
        return cachedDeviceId;
    }

    // Genera un nuovo deviceID (simile al pattern Swift)
    // Usa una combinazione di timestamp, user agent e random per creare un ID unico
    const timestamp = Date.now().toString();
    const userAgent = navigator.userAgent || 'unknown';
    const randomValue = Math.floor(Math.random() * 1000000).toString();
    
    // Crea un hash MD5 della combinazione per ottenere un ID pulito
    const deviceIdString = `web_${timestamp}_${randomValue}_${userAgent}`;
    const deviceId = CryptoJS.MD5(deviceIdString).toString(CryptoJS.enc.Hex);

    // Salva nel localStorage per persistenza
    localStorage.setItem('deviceID', deviceId);
    cachedDeviceId = deviceId;

    log.debug('📱 Generated new deviceID:', deviceId);
    return deviceId;
};

// Funzione per calcolare il sign
export const calculateSign = async (): Promise<{ requestId: string; sign: string }> => {
    const requestId = getTimestamp();
    const rs = await getRsFromRemoteConfig();

    // Crea la stringa da hashare: clientID + requestId + secretKey + rs
    const stringToHash = CLIENT_ID + requestId + SECRET_KEY + rs;

    //log.debug('🔐 String to hash:', stringToHash);

    // Calcola MD5 hash
    const sign = CryptoJS.MD5(stringToHash).toString(CryptoJS.enc.Hex);

    //log.debug('🔑 Generated sign:', sign);
    //log.debug('⏰ RequestId:', requestId);

    return { requestId, sign };
};

// Utility per resettare la cache (utile per testing)
export const resetRsCache = () => {
    cachedRs = null;
};

// Utility per resettare il deviceID cache
export const resetDeviceIdCache = () => {
    cachedDeviceId = null;
    localStorage.removeItem('deviceID');
};